import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Loader2, HardHat, Sun, Moon, Monitor } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const themeIcon = { light: Sun, dark: Moon, system: Monitor }[theme];
  const ThemeIcon = themeIcon;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left: Form */}
      <div className="flex flex-col justify-between p-6 lg:p-12">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md overflow-hidden bg-white ring-1 ring-border flex items-center justify-center shrink-0">
              <img src="/logo.png" alt="MMF Grup" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-heading font-bold text-lg tracking-tight leading-none">
                MMF Grup
              </div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-1">
                Yapsat Yönetim Sistemi
              </div>
            </div>
          </div>
          <button
            data-testid="theme-toggle-login"
            onClick={() =>
              setTheme(theme === "light" ? "dark" : theme === "dark" ? "system" : "light")
            }
            className="p-2 rounded-md border border-border hover:bg-secondary transition-colors"
            title={`Tema: ${theme}`}
          >
            <ThemeIcon className="w-4 h-4" />
          </button>
        </div>

        <div className="max-w-sm w-full mx-auto lg:mx-0 py-12">
          <div className="mb-8">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-2">
              Hoş geldiniz
            </h1>
            <p className="text-sm text-muted-foreground">
              Devam etmek için hesabınıza giriş yapın.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
                E-posta
              </Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="admin@yapsat.com"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
                Şifre
              </Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="h-11"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-11 font-semibold"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Giriş Yap
            </Button>
          </form>
        </div>

        <div className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} MMF Grup. Tüm hakları saklıdır.
        </div>
      </div>

      {/* Right: Hero */}
      <div className="hidden lg:block relative overflow-hidden bg-secondary">
        <img
          src="https://images.unsplash.com/photo-1479839672679-a46483c0e7c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb25zdHJ1Y3Rpb24lMjBhcmNoaXRlY3R1cmUlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODUwMjcyNzl8MA&ixlib=rb-4.1.0&q=85"
          alt="MMF Grup construction"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/30 to-transparent" />
        <div className="absolute inset-0 p-12 flex flex-col justify-end text-white">
          <div className="flex items-center gap-2 mb-4">
            <HardHat className="w-5 h-5 text-brand" />
            <span className="text-xs uppercase tracking-[0.2em] font-semibold">
              İnşaat Yönetimi
            </span>
          </div>
          <h2 className="font-heading text-4xl xl:text-5xl font-bold tracking-tight leading-[1.05] mb-4 max-w-md">
            Projelerinizi tek panelden yönetin.
          </h2>
          <p className="text-sm text-white/80 max-w-sm">
            Daireler, muhasebe, çekler, cariler ve hakedişler — tümü kurumsal
            standartlarda, tek yerde.
          </p>
        </div>
      </div>
    </div>
  );
}
